import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import CharacterCount from '@tiptap/extension-character-count';
import Link from '@tiptap/extension-link';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { ReactComponent as BoldIcon } from 'dist/icons/bold.svg';
import { ReactComponent as ItalicIcon } from 'dist/icons/italic.svg';
import { ReactComponent as OrderedListIcon } from 'dist/icons/ordered-list.svg';
import { ReactComponent as UnorderedListIcon } from 'dist/icons/unordered-list.svg';
import type { RichAreaProps, MenuBarProps } from './types';
import './style.scss';

const getImageFilesFromClipboard = (event: ClipboardEvent): File[] => {
  const cd = event.clipboardData;
  if (!cd) return [];

  const fromFiles: File[] = [];
  if (cd.files?.length) {
    for (let i = 0; i < cd.files.length; i += 1) {
      const f = cd.files[i];
      if (f.type.startsWith('image/')) fromFiles.push(f);
    }
  }
  if (fromFiles.length) return fromFiles;

  const fromItems: File[] = [];
  if (cd.items?.length) {
    for (let i = 0; i < cd.items.length; i += 1) {
      const item = cd.items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) fromItems.push(f);
      }
    }
  }
  return fromItems;
};

const MenuBar: React.FC<MenuBarProps> = ({ editor }) => {
  if (!editor) return null;
  return (
    <div className="rich-area-default__menu">
      <BoldIcon
        className={cn('rich-area__icon bold', { active: editor.isActive('bold') })}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ItalicIcon
        className={cn('rich-area__icon italic', { active: editor.isActive('italic') })}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <div className="divider" />
      <UnorderedListIcon
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn('rich-area__icon bulletList', { active: editor.isActive('bulletList') })}
      />
      <OrderedListIcon
        className={cn('rich-area__icon orderedList', { active: editor.isActive('orderedList') })}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
    </div>
  );
};

export const RichArea: React.FC<RichAreaProps> = ({
  className,
  initialValue = '',
  onChange,
  placeholder = 'Placeholder',
  maxLength = 2000,
  error,
  disabled,
  optional,
  autofocus = false,
  description,
  warning,
  required,
  label,
  onPasteImageFiles,
}) => {
  const [count, setCount] = useState(0);
  const onPasteImageFilesRef = useRef(onPasteImageFiles);
  onPasteImageFilesRef.current = onPasteImageFiles;

  const getEditorText = (e: { getText: () => string }) => e.getText().replace(/(\r\n|\n|\r)/gm, '');

  const onChangeValue = ({ editor: e }: { editor: { getHTML: () => string; getText: () => string } }) => {
    const text = getEditorText(e);
    if (text.length) {
      onChange?.(e.getHTML(), e.getText());
    } else {
      onChange?.('');
    }
    setCount(text.length);
  };

  const onCreateEditor = ({ editor: e }: { editor: { getHTML: () => string; getText: () => string } }) => {
    const text = getEditorText(e);
    setCount(text.length);
    if (text.length) {
      onChange?.(e.getHTML(), e.getText());
    }
  };

  const editor = useEditor(
    {
      editable: !disabled,
      autofocus,
      extensions: [
        StarterKit,
        Link.configure({
          autolink: true,
          linkOnPaste: true,
          openOnClick: false,
          defaultProtocol: 'https',
          protocols: ['http', 'https'],
          HTMLAttributes: {
            class: 'rich-area-default__link',
            rel: 'noopener noreferrer',
            target: '_blank',
          },
        }),
        CharacterCount.configure({ limit: maxLength }),
      ],
      content: initialValue,
      onUpdate: onChangeValue,
      onCreate: onCreateEditor,
    },
    [maxLength, autofocus, initialValue, disabled]
  );

  useEffect(() => {
    if (!editor || disabled) return undefined;

    const { dom } = editor.view;
    const onPasteCapture = (event: ClipboardEvent) => {
      if (!dom.contains(document.activeElement)) return;

      const cb = onPasteImageFilesRef.current;
      if (!cb) return;

      const imageFiles = getImageFilesFromClipboard(event);
      if (!imageFiles.length) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      cb(imageFiles);
    };

    dom.addEventListener('paste', onPasteCapture, true);
    return () => {
      dom.removeEventListener('paste', onPasteCapture, true);
    };
  }, [editor, disabled]);

  return (
    <div className={cn('rich-area-default', { warning }, { error }, { disabled }, className)}>
      <Typography.Label weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {required ? `${label} *` : label}
        {optional && <span className="optional"> (optional)</span>}
      </Typography.Label>
      {description && <Typography.Caption>{description}</Typography.Caption>}
      <div className="rich-area-default__rich-area">
        <MenuBar editor={editor} />
        {!count && <div className="placeholder">{placeholder}</div>}
        <EditorContent className="rich-area-default__rich-content" editor={editor} />
      </div>
      <Typography.Caption className="rich-area-default__counter">
        {count}/{maxLength}
      </Typography.Caption>
    </div>
  );
};
